import z from 'zod';
import { Agent } from './agent';
import type { AgentToolInvocation } from './agent-tool-invocation';
import { getAgentToolSourceAgent } from './agent-tool-source-registry';
import {
  CompactBoundaryRecordSchema,
  CompactSummaryRecordSchema,
} from './compaction/types';
import { SystemError, UserError } from './errors';
import type { InputGuardrailResult, OutputGuardrailResult } from './guardrail';
import { handoff } from './handoff';
import {
  RunCompactBoundaryItem,
  RunCompactSummaryItem,
  RunHandoffCallItem,
  RunHandoffOutputItem,
  type RunItem,
  RunMessageOutputItem,
  RunReasoningItem,
  RunToolApprovalItem,
  RunToolCallItem,
  RunToolCallOutputItem,
} from './items';
import logger from './logger';
import type { ModelResponse, ModelSettings } from './model';
import { RunContext } from './run-context';
import { getTurnInput, type ReasoningItemIdPolicy } from './runner/items';
import { type NextStep, nextStepSchema } from './runner/steps';
import { AgentToolUseTracker } from './runner/tool-use-tracker';
import type { ProcessedResponse } from './runner/types';
import { getSerializedFunctionToolPlaceholder } from './sandbox/runtime/tool-rehydration';
import { SANDBOX_SESSION_STATE_VERSION } from './sandbox/session';
import type { HostedMCPTool, Tool } from './tool';
import type {
  ToolInputGuardrailResult,
  ToolOutputGuardrailResult,
} from './tool-guardrails';
import {
  getFunctionToolQualifiedName,
  resolveFunctionToolCallName,
} from './tool-identity';
import { getCurrentTrace } from './tracing/context';
import { getGlobalTraceProvider } from './tracing/provider';
import type { AgentSpanData, Span } from './tracing/spans';
import type { Trace } from './tracing/traces';
import type { AgentInputItem, UnknownContext } from './types';
import * as protocol from './types/protocol';
import { Usage } from './usage';
import { safeExecute } from './utils/safe-execute';

export type FinalOutputSource = 'error_handler' | 'turn_resolution';

export type ActiveLocalCompactionState = {
  boundaryId: string;
  summaryItemId: string;
  baseModelContext: AgentInputItem[];
  generatedItemStartIndex: number;
};

const serializedSpanBase = z.object({
  object: z.literal('trace.span'),
  id: z.string(),
  trace_id: z.string(),
  parent_id: z.string().nullable(),
  started_at: z.string().nullable(),
  ended_at: z.string().nullable(),
  error: z
    .object({
      message: z.string(),
      data: z.record(z.string(), z.any()).optional(),
    })
    .nullable(),
  span_data: z.record(z.string(), z.any()),
});

type SerializedSpanType = z.infer<typeof serializedSpanBase> & {
  previous_span?: SerializedSpanType;
};

const SerializedSpan: z.ZodType<SerializedSpanType> = serializedSpanBase.extend(
  {
    previous_span: z.lazy(() => SerializedSpan).optional(),
  },
);

const sandboxSessionStateEnvelopeSchema = z.object({
  version: z.literal(SANDBOX_SESSION_STATE_VERSION),
  backendId: z.string(),
  manifest: z.record(z.string(), z.any()),
  snapshot: z.record(z.string(), z.any()).nullable().optional(),
  snapshotFingerprint: z.string().nullable().optional(),
  snapshotFingerprintVersion: z.string().nullable().optional(),
  workspaceReady: z.boolean(),
  exposedPorts: z.record(z.string(), z.any()).optional(),
  providerState: z.record(z.string(), z.any()),
});

const sandboxSessionEntrySchema = z.object({
  backendId: z.string(),
  currentAgentKey: z.string(),
  currentAgentName: z.string(),
  sessionState: sandboxSessionStateEnvelopeSchema,
  preservedOwnedSession: z.boolean().optional(),
  reuseLiveSession: z.boolean().optional(),
});

const sandboxStateSchema = z.object({
  backendId: z.string(),
  currentAgentKey: z.string(),
  currentAgentName: z.string(),
  sessionState: sandboxSessionStateEnvelopeSchema,
  sessionsByAgent: z.record(z.string(), sandboxSessionEntrySchema),
});

const serializedAgentSchema = z.object({
  name: z.string(),
  identity: z.string().optional(),
});
type SerializedAgentReference = z.infer<typeof serializedAgentSchema>;

const itemSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('message_output_item'),
    rawItem: protocol.AssistantMessageItem,
    agent: serializedAgentSchema,
  }),
  z.object({
    type: z.literal('tool_call_item'),
    rawItem: protocol.ToolCallItem.or(protocol.HostedToolCallItem),
    agent: serializedAgentSchema,
  }),
  z.object({
    type: z.literal('tool_call_output_item'),
    rawItem: protocol.FunctionCallResultItem,
    agent: serializedAgentSchema,
    output: z.string(),
  }),
  z.object({
    type: z.literal('reasoning_item'),
    rawItem: protocol.ReasoningItem,
    agent: serializedAgentSchema,
  }),
  z.object({
    type: z.literal('handoff_call_item'),
    rawItem: protocol.FunctionCallItem,
    agent: serializedAgentSchema,
  }),
  z.object({
    type: z.literal('handoff_output_item'),
    rawItem: protocol.FunctionCallResultItem,
    sourceAgent: serializedAgentSchema,
    targetAgent: serializedAgentSchema,
  }),
  z.object({
    type: z.literal('tool_approval_item'),
    rawItem: protocol.FunctionCallItem.or(protocol.HostedToolCallItem),
    agent: serializedAgentSchema,
    toolName: z.string().optional(),
  }),
  z.object({
    type: z.literal('compact_boundary_item'),
    rawItem: CompactBoundaryRecordSchema,
    agent: serializedAgentSchema,
  }),
  z.object({
    type: z.literal('compact_summary_item'),
    rawItem: CompactSummaryRecordSchema,
    agent: serializedAgentSchema,
  }),
]);

const requestUsageSchema = z.object({
  inputTokens: z.number(),
  outputTokens: z.number(),
  totalTokens: z.number(),
  inputTokensDetails: z.record(z.string(), z.number()).optional(),
  outputTokensDetails: z.record(z.string(), z.number()).optional(),
  endpoint: z.string().optional(),
});

const usageSchema = z.object({
  requests: z.number(),
  inputTokens: z.number(),
  outputTokens: z.number(),
  totalTokens: z.number(),
  inputTokensDetails: z.array(z.record(z.string(), z.number())).optional(),
  outputTokensDetails: z.array(z.record(z.string(), z.number())).optional(),
  requestUsageEntries: z.array(requestUsageSchema).optional(),
});

const modelResponseSchema = z.object({
  usage: usageSchema,
  output: z.array(protocol.OutputModelItem),
  responseId: z.string().optional(),
  requestId: z.string().optional(),
  providerData: z.record(z.string(), z.any()).optional(),
});

const activeLocalCompactionStateSchema = z.object({
  boundaryId: z.string(),
  summaryItemId: z.string(),
  baseModelContext: z.array(protocol.ModelItem),
  generatedItemStartIndex: z.number().int().min(0),
});

const guardrailFunctionOutputSchema = z.object({
  tripwireTriggered: z.boolean(),
  outputInfo: z.any(),
});

const inputGuardrailResultSchema = z.object({
  guardrail: z.object({
    type: z.literal('input'),
    name: z.string(),
  }),
  output: guardrailFunctionOutputSchema,
});

const outputGuardrailResultSchema = z.object({
  guardrail: z.object({
    type: z.literal('output'),
    name: z.string(),
  }),
  agentOutput: z.any(),
  agent: serializedAgentSchema,
  output: guardrailFunctionOutputSchema,
});

const toolGuardrailMetadataSchema = z.object({
  type: z.union([z.literal('tool_input'), z.literal('tool_output')]),
  name: z.string(),
});

const toolGuardrailBehaviorSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('allow') }),
  z.object({
    type: z.literal('rejectContent'),
    message: z.string(),
  }),
  z.object({ type: z.literal('throwException') }),
]);

const toolGuardrailFunctionOutputSchema = z.object({
  outputInfo: z.any().optional(),
  behavior: toolGuardrailBehaviorSchema,
});

const toolInputGuardrailResultSchema = z.object({
  guardrail: toolGuardrailMetadataSchema.extend({
    type: z.literal('tool_input'),
  }),
  output: toolGuardrailFunctionOutputSchema,
});

const toolOutputGuardrailResultSchema = z.object({
  guardrail: toolGuardrailMetadataSchema.extend({
    type: z.literal('tool_output'),
  }),
  output: toolGuardrailFunctionOutputSchema,
});

const serializedProcessedResponseSchema = z.object({
  newItems: z.array(itemSchema),
  toolsUsed: z.array(z.string()),
  handoffs: z.array(
    z.object({
      toolCall: z.any(),
      handoff: z.any(),
    }),
  ),
  functions: z.array(
    z.object({
      toolCall: z.any(),
      tool: z.any(),
    }),
  ),
  mcpApprovalRequests: z
    .array(
      z.object({
        requestItem: z.object({
          // protocol.HostedToolCallItem
          rawItem: z.object({
            type: z.literal('hosted_tool_call'),
            name: z.string(),
            arguments: z.string().optional(),
            status: z.string().optional(),
            output: z.string().optional(),
            // this always exists but marked as optional for early version compatibility; when releasing 1.0, we can remove the nullable and optional
            providerData: z.record(z.string(), z.any()).nullable().optional(),
          }),
        }),
        // HostedMCPTool
        mcpTool: z.object({
          type: z.literal('hosted_tool'),
          name: z.literal('hosted_mcp'),
          providerData: z.record(z.string(), z.any()),
        }),
      }),
    )
    .optional(),
});

const serializedTraceSchema = z.object({
  object: z.literal('trace'),
  id: z.string(),
  workflow_name: z.string(),
  group_id: z.string().nullable(),
  metadata: z.record(z.string(), z.any()),
  // Populated only if the trace was created with a per-run tracingApiKey (e.g., Runner.run({ tracing: { apiKey } }))
  // and serialization opts in to include it. By default this is omitted to avoid persisting secrets.
  tracing_api_key: z.string().optional().nullable(),
});

export const CURRENT_SCHEMA_VERSION = '1.11' as const;
const SUPPORTED_SCHEMA_VERSIONS = [
  '1.0',
  '1.1',
  '1.2',
  '1.3',
  '1.4',
  '1.5',
  '1.6',
  '1.7',
  '1.8',
  '1.9',
  '1.10',
  CURRENT_SCHEMA_VERSION,
] as const;
type SupportedSchemaVersion = (typeof SUPPORTED_SCHEMA_VERSIONS)[number];
const $schemaVersion = z.enum(SUPPORTED_SCHEMA_VERSIONS);
export const SerializedRunState = z.object({
  $schemaVersion,
  currentTurn: z.number(),
  currentAgent: serializedAgentSchema,
  originalInput: z.string().or(z.array(protocol.ModelItem)),
  modelResponses: z.array(modelResponseSchema),
  context: z.object({
    usage: usageSchema,
    approvals: z.record(
      z.string(),
      z.object({
        approved: z.array(z.string()).or(z.boolean()),
        rejected: z.array(z.string()).or(z.boolean()),
        messages: z.record(z.string(), z.string()).optional(),
        stickyRejectMessage: z.string().optional(),
        updatedInput: z.record(z.string(), z.any()).optional(),
      }),
    ),
    context: z.record(z.string(), z.any()),
    toolInput: z.any().optional(),
  }),
  toolUseTracker: z.record(z.string(), z.array(z.string())),
  maxTurns: z.number().nullable(),
  currentAgentSpan: SerializedSpan.nullable().optional(),
  noActiveAgentRun: z.boolean(),
  inputGuardrailResults: z.array(inputGuardrailResultSchema),
  outputGuardrailResults: z.array(outputGuardrailResultSchema),
  toolInputGuardrailResults: z
    .array(toolInputGuardrailResultSchema)
    .optional()
    .default([]),
  toolOutputGuardrailResults: z
    .array(toolOutputGuardrailResultSchema)
    .optional()
    .default([]),
  currentTurnInProgress: z.boolean().optional(),
  currentStep: nextStepSchema.optional(),
  lastModelResponse: modelResponseSchema.optional(),
  generatedItems: z.array(itemSchema),
  activeLocalCompaction: activeLocalCompactionStateSchema.optional(),
  pendingAgentToolRuns: z.record(z.string(), z.string()).optional().default({}),
  lastProcessedResponse: serializedProcessedResponseSchema.optional(),
  currentTurnPersistedItemCount: z.number().int().min(0).optional(),
  conversationId: z.string().optional(),
  previousResponseId: z.string().optional(),
  reasoningItemIdPolicy: z.enum(['preserve', 'omit']).optional(),
  trace: serializedTraceSchema.nullable(),
  sandbox: sandboxStateSchema.optional(),
});

/**
 * 智能体运行的可序列化快照，包含上下文、用量和追踪信息。
 * 虽然此类具有公开可写属性（以下划线 `_` 为前缀），但它们并不适合直接使用。
 * 要读取这些属性，请改用 `RunResult`。
 *
 * 直接操作状态可能会导致意外行为，应尽量避免。
 * 请改用 `approve` 和 `reject` 方法与状态进行交互。
 */
export class RunState<TContext, TAgent extends Agent<any, any>> {
  /**
   * 对话中的当前轮次数。
   */
  public _currentTurn = 0;

  /**
   * 当前轮次是否已被计数（在轮次中途恢复时很有用）。
   */
  public _currentTurnInProgress = false;
  /**
   * 当前正在处理对话的智能体。
   */
  public _currentAgent: TAgent;

  /**
   * 启动整个运行的根智能体。
   */
  #startingAgent: TAgent;

  /**
   * 在任何处理之前的原始用户输入。
   */
  public _originalInput: string | AgentInputItem[];

  /**
   * 到目前为止来自模型的响应。
   */
  public _modelResponses: ModelResponse[];

  /**
   * 当服务器管理对话历史记录时的对话标识符。
   */
  public _conversationId: string | undefined;

  /**
   * 对于由服务器管理的对话，服务器返回的最新响应标识符。
   */
  public _previousResponseId: string | undefined;
  // /**
  //  * 控制运行项如何转换为模型轮次输入的运行时选项。
  //  * 此值会被序列化，以便恢复的运行保持相同的轮次输入行为。
  //  */
  public _reasoningItemIdPolicy: ReasoningItemIdPolicy | undefined;

  /**
   * 最近一次模型调用所使用的有效模型设置。
   */
  public _lastModelSettings: ModelSettings | undefined;
  //
  /**
   * 如果启用了追踪，则为当前智能体的活动追踪 span。
   */
  public _currentAgentSpan: Span<AgentSpanData> | undefined;
  /**
   * 追踪审批、用量和其他元数据的运行上下文。
   */
  public _context: RunContext<TContext>;
  /**
   * 当前嵌套智能体工具调用的仅运行时元数据。
   */
  public _agentToolInvocation: AgentToolInvocation | undefined;

  /**
   * 本次运行聚合的用量信息。在可用时包含每个请求的细分数据。
   */
  get usage(): Usage {
    return this._context.usage;
  }

  /**
   * 追踪每个智能体使用了哪些工具。
   */
  public _toolUseTracker: AgentToolUseTracker;

  /**
   * 按工具名称和调用 ID 为键的、已序列化的待处理嵌套智能体运行。
   */
  public _pendingAgentToolRuns: Map<string, string>;
  /**
   * 运行期间智能体生成的项。
   */
  public _generatedItems: RunItem[];

  /**
   * 当前 run 内已生效的本地压缩上下文。它只影响后续模型输入，
   * 不裁剪 `_generatedItems`，从而保留完整 transcript/result。
   */
  public _activeLocalCompaction: ActiveLocalCompactionState | undefined;

  /**
   * 对于当前轮次，已刷新到会话存储的 `_generatedItems` 数量。
   *
   * 每次保存时都持久化整个轮次会导致响应和工具输出重复。
   * 因此，`saveToSession` 仅追加自上次写入以来的增量。此计数器
   * 追踪*本轮次*中有多少已生成的运行项已经被写入，以便下次
   * 保存时可以只切分出新的条目。当轮次被中断（例如，等待工具
   * 审批）并稍后恢复时，我们会在继续之前回退此计数器，以便待处理的工具
   * 输出仍能被存储。
   */
  public _currentTurnPersistedItemCount: number;

  /**
   * 强制终止前允许的最大轮次数。
   */
  public _maxTurns: number | null;

  /**
   * 运行是否有一个正在进行的活动智能体步骤。
   */
  public _noActiveAgentRun = true;

  /**
   * 上一轮次的最后一个模型响应。
   */
  public _lastTurnResponse: ModelResponse | undefined;

  /**
   * 应用于此运行的输入护栏结果。
   */
  public _inputGuardrailResults: InputGuardrailResult[];
  /**
   * 应用于此运行的输出护栏结果。
   */
  public _outputGuardrailResults: OutputGuardrailResult<any, any>[];
  /**
   * 在工具执行期间应用的工具输入护栏结果。
   */
  public _toolInputGuardrailResults: ToolInputGuardrailResult[];
  /**
   * 在工具执行期间应用的工具输出护栏结果。
   */
  public _toolOutputGuardrailResults: ToolOutputGuardrailResult[];
  /**
   * 为智能体计算出的下一步操作。
   */
  public _currentStep: NextStep | undefined = undefined;

  /**
   * 指示当前运行的最终输出是如何产生的。
   * 此值不会被序列化。
   */
  public _finalOutputSource: FinalOutputSource | undefined;
  //
  /**
   * 应用护栏和工具后解析出的模型响应。
   */
  public _lastProcessedResponse: ProcessedResponse<TContext> | undefined =
    undefined;

  /**
   * 如果启用了追踪，则为此运行关联的追踪对象。
   */
  public _trace: Trace | null = null;
  // /**
  //  * 仅运行时存在的、由 tool_search 加载的工具，按智能体对象限定作用域，并在此内存运行
  //  * 的生命周期内跨轮次保留。
  //  */
  // public _toolSearchRuntimeToolsByAgent = new Map<
  //   Agent<any, any>,
  //   ToolSearchRuntimeToolState<TContext>
  // >();
  /**
   * 用于沙箱智能体恢复的、已持久化的沙箱会话元数据。
   */
  public _sandbox: z.infer<typeof sandboxStateSchema> | undefined = undefined;

  constructor(
    context: RunContext<TContext>,
    originalInput: string | AgentInputItem[],
    startingAgent: TAgent,
    maxTurns: number | null,
  ) {
    this._context = context;
    this._agentToolInvocation = undefined;
    this._originalInput = structuredClone(originalInput);
    this._modelResponses = [];
    this._currentAgentSpan = undefined;
    this._currentAgent = startingAgent;
    this.#startingAgent = startingAgent;
    this._reasoningItemIdPolicy = undefined;
    this._toolUseTracker = new AgentToolUseTracker();
    this._pendingAgentToolRuns = new Map();
    this._generatedItems = [];
    this._currentTurnPersistedItemCount = 0;
    this._maxTurns = maxTurns;
    this._inputGuardrailResults = [];
    this._outputGuardrailResults = [];
    this._toolInputGuardrailResults = [];
    this._toolOutputGuardrailResults = [];
    this._trace = getCurrentTrace();
  }

  /**
   * 将服务器管理的对话标识符作为一个整体操作进行更新。
   */
  public setConversationContext(
    conversationId?: string,
    previousResponseId?: string,
  ): void {
    this._conversationId = conversationId;
    this._previousResponseId = previousResponseId;
  }

  /**
   * 更新用于将运行项转换为轮次输入的运行时选项。
   */
  public setReasoningItemIdPolicy(policy?: ReasoningItemIdPolicy): void {
    this._reasoningItemIdPolicy = policy;
  }

  /**
   * 更新与当前运行关联的智能体 span。
   */
  public setCurrentAgentSpan(span?: Span<AgentSpanData>): void {
    this._currentAgentSpan = span;
  }

  // private getOrCreateToolSearchRuntimeToolState(
  //   agent: Agent<any, any>,
  // ): ToolSearchRuntimeToolState<TContext> {
  //   let state = this._toolSearchRuntimeToolsByAgent.get(agent);
  //   if (!state) {
  //     state = {
  //       anonymousEntries: [],
  //       keyedEntries: new Map(),
  //       nextOrder: 0,
  //     };
  //     this._toolSearchRuntimeToolsByAgent.set(agent, state);
  //   }
  //   return state;
  // }
  //
  // public recordToolSearchRuntimeTools(
  //   agent: Agent<any, any>,
  //   toolSearchOutput: protocol.ToolSearchOutputItem,
  //   tools: Tool<TContext>[],
  // ): void {
  //   const runtimeState = this.getOrCreateToolSearchRuntimeToolState(agent);
  //   const entry: ToolSearchRuntimeToolEntry<TContext> = {
  //     order: runtimeState.nextOrder++,
  //     tools,
  //   };
  //   const replacementKey = getToolSearchOutputReplacementKey(toolSearchOutput);
  //   if (replacementKey) {
  //     runtimeState.keyedEntries.set(replacementKey, entry);
  //     return;
  //   }
  //
  //   runtimeState.anonymousEntries.push(entry);
  // }
  //
  // public getToolSearchRuntimeTools(agent: Agent<any, any>): Tool<TContext>[] {
  //   const runtimeState = this._toolSearchRuntimeToolsByAgent.get(agent);
  //   if (!runtimeState) {
  //     return [];
  //   }
  //
  //   const dedupedTools = new Map<string, Tool<TContext>>();
  //   const orderedEntries = [
  //     ...runtimeState.keyedEntries.values(),
  //     ...runtimeState.anonymousEntries,
  //   ].sort((a, b) => a.order - b.order);
  //   let anonymousCounter = 0;
  //
  //   for (const entry of orderedEntries) {
  //     for (const tool of entry.tools) {
  //       const key =
  //         getToolSearchRuntimeToolKey(tool) ??
  //         `anonymous:${entry.order}:${anonymousCounter++}`;
  //       dedupedTools.set(key, tool);
  //     }
  //   }
  //
  //   return [...dedupedTools.values()];
  // }

  /**
   * 切换正在处理运行的活动智能体。
   */
  public setCurrentAgent(agent: TAgent): void {
    this._currentAgent = agent;
  }

  /**
   * 返回当前正在处理运行的智能体。
   */
  get currentAgent(): TAgent {
    return this._currentAgent;
  }

  /**
   * 重置追踪当前轮次已持久化多少项的计数器。
   */
  public resetTurnPersistence(): void {
    this._currentTurnPersistedItemCount = 0;
  }

  /**
   * 当有待处理的审批需要重写输出时，回退已持久化项的计数器。
   */
  public rewindTurnPersistence(count: number): void {
    if (count <= 0) {
      return;
    }
    this._currentTurnPersistedItemCount = Math.max(
      0,
      this._currentTurnPersistedItemCount - count,
    );
  }

  /**
   * 智能体运行的历史记录。包括输入项和运行期间生成的新项。
   *
   * 这可以用作下一次智能体运行的输入。
   */
  get history(): AgentInputItem[] {
    return getTurnInput(
      this._originalInput,
      this._generatedItems,
      this._reasoningItemIdPolicy,
    );
  }

  /**
   * 如果当前步骤是一个中断，则返回所有中断；否则返回一个空数组。
   */
  getInterruptions(): RunToolApprovalItem[] {
    if (this._currentStep?.type !== 'next_step_interruption') {
      return [];
    }
    const interruptions = this._currentStep.data.interruptions;
    return Array.isArray(interruptions)
      ? (interruptions as RunToolApprovalItem[])
      : [];
  }

  private getPendingAgentToolRunKey(toolName: string, callId: string): string {
    return `${toolName}:${callId}`;
  }

  getPendingAgentToolRun(toolName: string, callId: string): string | undefined {
    return this._pendingAgentToolRuns.get(
      this.getPendingAgentToolRunKey(toolName, callId),
    );
  }

  hasPendingAgentToolRun(toolName: string, callId: string): boolean {
    return this._pendingAgentToolRuns.has(
      this.getPendingAgentToolRunKey(toolName, callId),
    );
  }

  setPendingAgentToolRun(
    toolName: string,
    callId: string,
    serializedState: string,
  ) {
    this._pendingAgentToolRuns.set(
      this.getPendingAgentToolRunKey(toolName, callId),
      serializedState,
    );
  }

  clearPendingAgentToolRun(toolName: string, callId: string) {
    this._pendingAgentToolRuns.delete(
      this.getPendingAgentToolRunKey(toolName, callId),
    );
  }

  /**
   * 批准智能体通过中断和审批项请求发出的工具调用。
   *
   * 要批准请求，请使用此方法，然后使用相同的状态对象再次运行智能体
   * 以继续执行。
   *
   * 默认情况下，它只会批准当前的这一次工具调用。要允许在此次运行的整个过程中
   * 多次使用该工具，请将 `alwaysApprove` 选项设置为 `true`。
   *
   * @param approvalItem - 要批准的工具调用审批项。
   * @param options - 批准选项。
   * @param options.alwaysApprove - 批准此工具在本次运行的所有后续调用中使用。
   */
  approve(
    approvalItem: RunToolApprovalItem,
    options: { alwaysApprove?: boolean; updatedInput?: unknown } = {
      alwaysApprove: false,
    },
  ) {
    this._context.approveTool(approvalItem, options);
  }

  /**
   * 拒绝智能体通过中断和审批项请求发出的工具调用。
   *
   * 要拒绝请求，请使用此方法，然后使用相同的状态对象再次运行智能体
   * 以继续执行。
   *
   * 默认情况下，它只会拒绝当前的这一次工具调用。要拒绝此工具在本次运行的
   * 所有后续调用中使用，请将 `alwaysReject` 选项设置为 `true`。
   *
   * 当提供 `message` 时，它会被用作发送给模型的拒绝文本。
   * 否则，将使用 `toolErrorFormatter`（如果已配置）或 SDK 默认值。
   *
   * @param approvalItem - 要拒绝的工具调用审批项。
   * @param options - 拒绝选项。
   * @param options.alwaysReject - 拒绝此工具在本次运行的所有后续调用中使用。
   * @param options.message - 发送给模型的拒绝文本。
   *   如果未提供，则使用 `toolErrorFormatter`（如果已配置）或 SDK 默认值。
   */
  reject(
    approvalItem: RunToolApprovalItem,
    options: { alwaysReject?: boolean; message?: string } = {
      alwaysReject: false,
    },
  ) {
    this._context.rejectTool(approvalItem, options);
  }

  /**
   * 将运行状态序列化为 JSON 对象。
   *
   * 此方法用于将运行状态序列化为一个 JSON 对象，该对象可用于稍后恢复运行。
   *
   * @returns 序列化后的运行状态。
   */
  /**
   * 序列化运行状态。默认情况下，会省略追踪 API 密钥，以防止
   * 机密信息被意外持久化。仅当您有意需要迁移运行及其追踪凭证时
   * （例如，在缺少原始环境变量的独立进程中还原），才传入 `includeTracingApiKey: true`。
   */
  toJSON(
    options: { includeTracingApiKey?: boolean } = {},
  ): z.infer<typeof SerializedRunState> {
    const agentIdentity = buildAgentIdentityMap(this.#startingAgent);

    const includeTracingApiKey = options.includeTracingApiKey === true;
    const contextJson = this._context.toJSON();
    const output = {
      $schemaVersion: CURRENT_SCHEMA_VERSION,
      currentTurn: this._currentTurn,
      currentAgent: serializeAgentReference(
        this._currentAgent,
        agentIdentity.byAgent,
      ),
      originalInput: this._originalInput as any,
      modelResponses: this._modelResponses.map((response) => {
        return {
          usage: {
            requests: response.usage.requests,
            inputTokens: response.usage.inputTokens,
            outputTokens: response.usage.outputTokens,
            totalTokens: response.usage.totalTokens,
            inputTokensDetails: response.usage.inputTokensDetails,
            outputTokensDetails: response.usage.outputTokensDetails,
            ...(response.usage.requestUsageEntries &&
            response.usage.requestUsageEntries.length > 0
              ? {
                  requestUsageEntries: response.usage.requestUsageEntries.map(
                    (entry) => ({
                      inputTokens: entry.inputTokens,
                      outputTokens: entry.outputTokens,
                      totalTokens: entry.totalTokens,
                      inputTokensDetails: entry.inputTokensDetails,
                      outputTokensDetails: entry.outputTokensDetails,
                      ...(entry.endpoint ? { endpoint: entry.endpoint } : {}),
                    }),
                  ),
                }
              : {}),
          },
          output: response.output as any,
          responseId: response.responseId,
          requestId: response.requestId,
          providerData: response.providerData,
        };
      }),
      context: contextJson,
      toolUseTracker: this._toolUseTracker.toJSON({
        agentIdentityKeys: agentIdentity.byAgent,
      }),
      maxTurns: this._maxTurns,
      currentAgentSpan: this._currentAgentSpan?.toJSON() as any,
      noActiveAgentRun: this._noActiveAgentRun,
      currentTurnInProgress: this._currentTurnInProgress,
      inputGuardrailResults: this._inputGuardrailResults,
      outputGuardrailResults: this._outputGuardrailResults.map((r) => ({
        ...r,
        agent: serializeAgentReference(r.agent, agentIdentity.byAgent),
      })),
      toolInputGuardrailResults: this._toolInputGuardrailResults,
      toolOutputGuardrailResults: this._toolOutputGuardrailResults,
      currentStep: serializeCurrentStep(
        this._currentStep,
        agentIdentity.byAgent,
      ) as any,
      lastModelResponse: this._lastTurnResponse as any,
      generatedItems: this._generatedItems.map(
        (item) => serializeRunItem(item, agentIdentity.byAgent) as any,
      ),
      activeLocalCompaction: this._activeLocalCompaction,
      pendingAgentToolRuns: Object.fromEntries(
        this._pendingAgentToolRuns.entries(),
      ),
      currentTurnPersistedItemCount: this._currentTurnPersistedItemCount,
      lastProcessedResponse: this._lastProcessedResponse
        ? (serializeProcessedResponse(
            this._lastProcessedResponse,
            agentIdentity.byAgent,
          ) as any)
        : undefined,
      conversationId: this._conversationId,
      previousResponseId: this._previousResponseId,
      reasoningItemIdPolicy: this._reasoningItemIdPolicy,
      trace: this._trace
        ? (this._trace.toJSON({ includeTracingApiKey }) as any)
        : null,
      sandbox: this._sandbox,
    };

    // 解析 schema 以确保输出可以被有效地重新解析
    const parsed = SerializedRunState.safeParse(output);
    if (!parsed.success) {
      throw new SystemError(`无法序列化运行状态。${parsed.error.message}`);
    }

    return parsed.data;
  }

  /**
   * 将运行状态序列化为字符串。
   *
   * 此方法用于将运行状态序列化为一个字符串，该字符串可用于稍后恢复运行。
   *
   * @returns 序列化后的运行状态。
   */
  toString(options: { includeTracingApiKey?: boolean } = {}) {
    return JSON.stringify(this.toJSON(options));
  }

  /**
   * 从字符串反序列化一个运行状态。
   *
   * 此方法用于从使用 `toString` 方法序列化的字符串中反序列化运行状态。
   */
  static async fromString<TContext, TAgent extends Agent<any, any>>(
    initialAgent: TAgent,
    str: string,
  ): Promise<RunState<TContext, TAgent>> {
    return buildRunStateFromString(initialAgent, str);
  }

  static async fromStringWithContext<TContext, TAgent extends Agent<any, any>>(
    initialAgent: TAgent,
    str: string,
    context: RunContext<TContext>,
    options: { contextStrategy?: ContextOverrideStrategy } = {},
  ): Promise<RunState<TContext, TAgent>> {
    return buildRunStateFromString(initialAgent, str, {
      contextOverride: context,
      contextStrategy: options.contextStrategy,
    });
  }
}

export async function rehydrateProcessedResponseTools<
  TContext,
  TAgent extends Agent<any, any>,
>(
  initialAgent: TAgent,
  state: RunState<TContext, TAgent>,
  executionTools: Tool<TContext>[],
): Promise<void> {
  if (!state._lastProcessedResponse) {
    return;
  }

  const agentIdentity = buildAgentIdentityMap(initialAgent);
  const serializedProcessedResponse = serializeProcessedResponse(
    state._lastProcessedResponse,
    agentIdentity.byAgent,
  );

  state._lastProcessedResponse = await deserializeProcessedResponse(
    agentIdentity.byIdentity,
    state as RunState<TContext, Agent<any, any>>,
    serializedProcessedResponse,
    {
      executionTools,
      allowSerializedExecutionToolPlaceholder: false,
    },
  );
}

type AgentIdentityMap = {
  byIdentity: Map<string, Agent<any, any>>;
  byAgent: Map<Agent<any, any>, string>;
};

type TraversedAgent = {
  agent: Agent<any, any>;
  index: number;
};

export function buildAgentIdentityMap(
  initialAgent: Agent<any, any>,
): AgentIdentityMap {
  const agents = collectAgentGraph(initialAgent);
  const groups = new Map<string, TraversedAgent[]>();
  const literalNames = new Set<string>();

  for (const entry of agents) {
    literalNames.add(entry.agent.name);
    const group = groups.get(entry.agent.name) ?? [];
    group.push(entry);
    groups.set(entry.agent.name, group);
  }

  const byIdentity = new Map<string, Agent<any, any>>();
  const byAgent = new Map<Agent<any, any>, string>();
  const usedIdentities = new Set<string>();

  for (const [agentName, group] of groups) {
    const sortedGroup =
      group.length === 1
        ? group
        : [...group].sort((left, right) => {
            if (left.agent === initialAgent) {
              return -1;
            }
            if (right.agent === initialAgent) {
              return 1;
            }

            const leftSignature = getAgentIdentitySignature(left.agent);
            const rightSignature = getAgentIdentitySignature(right.agent);
            if (leftSignature !== rightSignature) {
              return leftSignature < rightSignature ? -1 : 1;
            }

            return left.index - right.index;
          });

    let nextSuffix = 0;
    for (const { agent } of sortedGroup) {
      let identity: string;
      do {
        identity =
          nextSuffix === 0 ? agentName : `${agentName}#${nextSuffix + 1}`;
        nextSuffix += 1;
      } while (
        usedIdentities.has(identity) ||
        (identity !== agent.name && literalNames.has(identity))
      );

      usedIdentities.add(identity);
      byIdentity.set(identity, agent);
      byAgent.set(agent, identity);
    }
  }

  return { byIdentity, byAgent };
}

function collectAgentGraph(initialAgent: Agent<any, any>): TraversedAgent[] {
  const agents: TraversedAgent[] = [];
  const visitedAgents = new Set<Agent<any, any>>();
  const queue: Agent<any, any>[] = [initialAgent];

  while (queue.length > 0) {
    const currentAgent = queue.shift()!;
    if (visitedAgents.has(currentAgent)) {
      continue;
    }
    visitedAgents.add(currentAgent);
    agents.push({ agent: currentAgent, index: agents.length });

    for (const handoff of currentAgent.handoffs) {
      if (handoff instanceof Agent) {
        queue.push(handoff);
      } else if (handoff.agent) {
        queue.push(handoff.agent);
      }
    }

    for (const tool of currentAgent.tools) {
      const sourceAgent = getAgentToolSourceAgent(tool);
      if (sourceAgent) {
        queue.push(sourceAgent);
      }
    }
  }

  return agents;
}

function serializeProcessedResponse<TContext>(
  processedResponse: ProcessedResponse<TContext>,
  agentIdentityKeys: ReadonlyMap<Agent<any, any>, string>,
): z.infer<typeof serializedProcessedResponseSchema> {
  return {
    ...processedResponse,
    newItems: processedResponse.newItems.map((item) =>
      serializeRunItem(item, agentIdentityKeys),
    ),
  } as z.infer<typeof serializedProcessedResponseSchema>;
}

type DeserializeProcessedResponseOptions<TContext> = {
  executionTools?: Tool<TContext>[];
  allowSerializedExecutionToolPlaceholder?: boolean;
};

async function deserializeProcessedResponse<TContext = UnknownContext>(
  agentMap: Map<string, Agent<any, any>>,
  state: RunState<TContext, Agent<any, any>>,
  serializedProcessedResponse: z.infer<
    typeof serializedProcessedResponseSchema
  >,
  options: DeserializeProcessedResponseOptions<TContext> = {},
): Promise<ProcessedResponse<TContext>> {
  const currentAgent = state._currentAgent;
  const configuredTools =
    options.executionTools ?? (await currentAgent.getAllTools(state._context));
  const allTools = [...(configuredTools as Tool<TContext>[])];
  const baseAgentTools = currentAgent.tools as Tool<TContext>[];
  const allowSerializedExecutionToolPlaceholder =
    options.allowSerializedExecutionToolPlaceholder ?? true;
  const tools = new Map(
    allTools
      .filter((tool) => tool.type === 'function')
      .map((tool) => [getFunctionToolQualifiedName(tool) ?? tool.name, tool]),
  );
  const handoffs = new Map(
    currentAgent.handoffs.map((entry) => {
      if (entry instanceof Agent) {
        return [entry.name, handoff(entry)];
      }

      return [entry.toolName, entry];
    }),
  );

  const result = {
    newItems: serializedProcessedResponse.newItems.map((item) =>
      deserializeItem(item, agentMap),
    ),
    toolsUsed: serializedProcessedResponse.toolsUsed,
    handoffs: serializedProcessedResponse.handoffs.map((handoff) => {
      if (!handoffs.has(handoff.handoff.toolName)) {
        throw new UserError(`Handoff ${handoff.handoff.toolName} not found`);
      }

      return {
        toolCall: handoff.toolCall,
        handoff: handoffs.get(handoff.handoff.toolName)!,
      };
    }),
    functions: await Promise.all(
      serializedProcessedResponse.functions.map(async (functionCall) => {
        const toolIdentity =
          resolveFunctionToolCallName(functionCall.toolCall, tools) ??
          functionCall.tool.name;
        const resolvedTool =
          tools.get(toolIdentity) ??
          getSerializedFunctionToolPlaceholder({
            agent: currentAgent,
            baseAgentTools,
            serializedTool: functionCall.tool,
            toolCall: functionCall.toolCall,
            toolIdentity,
            allowSerializedExecutionToolPlaceholder,
          });
        if (!resolvedTool) {
          throw new UserError(`Tool ${toolIdentity} not found`);
        }

        return {
          toolCall: functionCall.toolCall,
          tool: resolvedTool,
        };
      }),
    ),
    mcpApprovalRequests: (
      serializedProcessedResponse.mcpApprovalRequests ?? []
    ).map((approvalRequest) => ({
      requestItem: new RunToolApprovalItem(
        approvalRequest.requestItem
          .rawItem as unknown as protocol.HostedToolCallItem,
        currentAgent,
      ),
      mcpTool: approvalRequest.mcpTool as unknown as HostedMCPTool,
    })),
  };

  return {
    ...result,
    hasToolsOrApprovalsToRun(): boolean {
      return (
        result.handoffs.length > 0 ||
        result.functions.length > 0 ||
        result.mcpApprovalRequests.length > 0
      );
    },
  };
}

function getAgentIdentitySignature(agent: Agent<any, any>): string {
  const sandboxAgent = agent as Agent<any, any> & {
    defaultManifest?: unknown;
    baseInstructions?: unknown;
    capabilities?: unknown[];
    runAs?: unknown;
  };
  const signature = {
    type: agent.constructor?.name,
    name: agent.name,
    handoffDescription: agent.handoffDescription,
    instructions: summarizeIdentityValue(agent.instructions),
    model: summarizeIdentityValue(agent.model),
    modelSettings: summarizeIdentityValue(agent.modelSettings),
    tools: agent.tools.map(summarizeToolIdentity),
    handoffs: agent.handoffs.map((entry) =>
      entry instanceof Agent
        ? { type: 'agent', name: entry.name }
        : {
            type: 'handoff',
            toolName: entry.toolName,
            agentName: entry.agentName,
            targetName: entry.agent?.name,
          },
    ),
    mcpServers: agent.mcpServers.map(summarizeIdentityValue),
    inputGuardrails: agent.inputGuardrails.map(summarizeIdentityValue),
    outputGuardrails: agent.outputGuardrails.map(summarizeIdentityValue),
    outputType: summarizeIdentityValue(agent.outputType),
    toolUseBehavior: summarizeIdentityValue(agent.toolUseBehavior),
    resetToolChoice: agent.resetToolChoice,
    defaultManifest: summarizeIdentityValue(sandboxAgent.defaultManifest),
    baseInstructions: summarizeIdentityValue(sandboxAgent.baseInstructions),
    capabilities: sandboxAgent.capabilities?.map(summarizeIdentityValue),
    runAs: summarizeIdentityValue(sandboxAgent.runAs),
  };

  return stableStringify(signature);
}

function summarizeIdentityValue(value: unknown): unknown {
  return normalizeForIdentity(value, new WeakSet(), 0);
}

function normalizeForIdentity(
  value: unknown,
  seen: WeakSet<object>,
  depth: number,
): unknown {
  if (value === null || typeof value === 'undefined') {
    return value;
  }
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return value;
  }
  if (typeof value === 'function') {
    return `[function:${value.name || 'anonymous'}]`;
  }
  if (typeof value !== 'object') {
    return String(value);
  }
  if (seen.has(value)) {
    return '[circular]';
  }
  if (depth >= 4) {
    return `[${value.constructor?.name ?? 'Object'}]`;
  }

  seen.add(value);
  if (Array.isArray(value)) {
    return value.map((item) => normalizeForIdentity(item, seen, depth + 1));
  }
  if (value instanceof Map) {
    return [...value.entries()]
      .map(([key, entryValue]) => [
        normalizeForIdentity(key, seen, depth + 1),
        normalizeForIdentity(entryValue, seen, depth + 1),
      ])
      .sort((left, right) =>
        stableStringify(left).localeCompare(stableStringify(right)),
      );
  }
  if (value instanceof Set) {
    return [...value.values()]
      .map((entry) => normalizeForIdentity(entry, seen, depth + 1))
      .sort((left, right) =>
        stableStringify(left).localeCompare(stableStringify(right)),
      );
  }

  const record = value as Record<string, unknown>;
  const normalized: Record<string, unknown> = {
    constructor: value.constructor?.name,
  };
  for (const key of Object.keys(record).sort()) {
    normalized[key] = normalizeForIdentity(record[key], seen, depth + 1);
  }
  return normalized;
}

function stableStringify(value: unknown): string {
  return JSON.stringify(value, (_key, currentValue) => {
    if (
      !currentValue ||
      typeof currentValue !== 'object' ||
      Array.isArray(currentValue)
    ) {
      return currentValue;
    }

    return Object.fromEntries(
      Object.entries(currentValue as Record<string, unknown>).sort(
        ([left], [right]) => left.localeCompare(right),
      ),
    );
  });
}

function serializeRunItem(
  item: RunItem,
  agentIdentityKeys: ReadonlyMap<Agent<any, any>, string>,
): z.infer<typeof itemSchema> {
  const serialized = item.toJSON() as any;
  switch (item.type) {
    case 'handoff_output_item':
      serialized.sourceAgent = serializeAgentReference(
        item.sourceAgent,
        agentIdentityKeys,
      );
      serialized.targetAgent = serializeAgentReference(
        item.targetAgent,
        agentIdentityKeys,
      );
      return serialized;
    case 'message_output_item':
    case 'tool_call_item':
    case 'tool_call_output_item':
    case 'reasoning_item':
    case 'handoff_call_item':
    case 'tool_approval_item':
    case 'compact_boundary_item':
    case 'compact_summary_item':
      serialized.agent = serializeAgentReference(item.agent, agentIdentityKeys);
      return serialized;
    default:
      item satisfies never;
      return serialized;
  }
}

function serializeAgentReference(
  agent: Agent<any, any>,
  agentIdentityKeys: ReadonlyMap<Agent<any, any>, string>,
): SerializedAgentReference {
  const identity = agentIdentityKeys.get(agent);
  if (!identity || identity === agent.name) {
    return { name: agent.name };
  }

  return { name: agent.name, identity };
}

export function deserializeItem(
  serializedItem: z.infer<typeof itemSchema>,
  agentMap: Map<string, Agent<any, any>>,
): RunItem {
  switch (serializedItem.type) {
    case 'message_output_item':
      return new RunMessageOutputItem(
        serializedItem.rawItem,
        resolveSerializedAgent(serializedItem.agent, agentMap),
      );
    case 'tool_call_item':
      return new RunToolCallItem(
        serializedItem.rawItem,
        resolveSerializedAgent(serializedItem.agent, agentMap),
      );
    case 'tool_call_output_item':
      return new RunToolCallOutputItem(
        serializedItem.rawItem,
        resolveSerializedAgent(serializedItem.agent, agentMap),
        serializedItem.output,
      );
    case 'reasoning_item':
      return new RunReasoningItem(
        serializedItem.rawItem,
        resolveSerializedAgent(serializedItem.agent, agentMap),
      );
    case 'handoff_call_item':
      return new RunHandoffCallItem(
        serializedItem.rawItem,
        resolveSerializedAgent(serializedItem.agent, agentMap),
      );
    case 'handoff_output_item':
      return new RunHandoffOutputItem(
        serializedItem.rawItem,
        resolveSerializedAgent(serializedItem.sourceAgent, agentMap),
        resolveSerializedAgent(serializedItem.targetAgent, agentMap),
      );
    case 'tool_approval_item':
      return new RunToolApprovalItem(
        serializedItem.rawItem,
        resolveSerializedAgent(serializedItem.agent, agentMap),
        serializedItem.toolName,
      );
    case 'compact_boundary_item':
      return new RunCompactBoundaryItem(
        serializedItem.rawItem,
        resolveSerializedAgent(serializedItem.agent, agentMap),
      );
    case 'compact_summary_item':
      return new RunCompactSummaryItem(
        serializedItem.rawItem,
        resolveSerializedAgent(serializedItem.agent, agentMap),
      );
  }
}

function resolveSerializedAgent(
  serializedAgent: SerializedAgentReference,
  agentMap: Map<string, Agent<any, any>>,
  fallbackAgent?: Agent<any, any>,
): Agent<any, any> {
  const identity = serializedAgent.identity ?? serializedAgent.name;
  const agent = agentMap.get(identity);
  if (agent) {
    return agent;
  }
  if (!serializedAgent.identity && fallbackAgent) {
    return fallbackAgent;
  }
  if (serializedAgent.identity) {
    throw new UserError(`Agent identity ${serializedAgent.identity} not found`);
  }
  throw new UserError(`Agent ${serializedAgent.name} not found`);
}

function summarizeToolIdentity(tool: Tool<any>): unknown {
  return {
    type: tool.type,
    name: (tool as { name?: unknown }).name,
    namespace: (tool as { namespace?: unknown }).namespace,
    strict: (tool as { strict?: unknown }).strict,
    parameters: summarizeIdentityValue(
      (tool as { parameters?: unknown }).parameters,
    ),
  };
}

function serializeCurrentStep(
  currentStep: NextStep | undefined,
  agentIdentityKeys: ReadonlyMap<Agent<any, any>, string>,
): NextStep | undefined {
  if (!currentStep) {
    return undefined;
  }
  if (currentStep.type === 'next_step_handoff') {
    return {
      ...currentStep,
      newAgent: serializeAgentReference(
        currentStep.newAgent as Agent<any, any>,
        agentIdentityKeys,
      ),
    };
  }
  if (currentStep.type === 'next_step_interruption') {
    const interruptions = Array.isArray(currentStep.data?.interruptions)
      ? currentStep.data.interruptions.map((item: unknown) =>
          item instanceof RunToolApprovalItem
            ? serializeRunItem(item, agentIdentityKeys)
            : item,
        )
      : currentStep.data?.interruptions;
    return {
      ...currentStep,
      data: {
        ...currentStep.data,
        interruptions,
      },
    };
  }

  return currentStep;
}

export function buildAgentMap(
  initialAgent: Agent<any, any>,
): Map<string, Agent<any, any>> {
  const map = new Map<string, Agent<any, any>>();
  const visitedAgents = new Set<Agent<any, any>>();
  const queue: Agent<any, any>[] = [initialAgent];

  while (queue.length > 0) {
    const currentAgent = queue.shift()!;
    if (visitedAgents.has(currentAgent)) {
      continue;
    }
    visitedAgents.add(currentAgent);

    const existingAgent = map.get(currentAgent.name);
    if (existingAgent && existingAgent !== currentAgent) {
      throw new UserError(
        `Duplicate agent name "${currentAgent.name}" detected. Use unique agent names when serializing RunState.`,
      );
    }

    map.set(currentAgent.name, currentAgent);

    for (const handoff of currentAgent.handoffs) {
      if (handoff instanceof Agent) {
        queue.push(handoff);
      } else if (handoff.agent) {
        queue.push(handoff.agent);
      }
    }

    for (const tool of currentAgent.tools) {
      const sourceAgent = getAgentToolSourceAgent(tool);
      if (sourceAgent) {
        queue.push(sourceAgent);
      }
    }
  }

  return map;
}

export function deserializeModelResponse(
  serializedModelResponse: z.infer<typeof modelResponseSchema>,
): ModelResponse {
  const usage = new Usage(serializedModelResponse.usage);

  return {
    usage,
    output: serializedModelResponse.output.map((item) =>
      protocol.OutputModelItem.parse(item),
    ),
    responseId: serializedModelResponse.responseId,
    requestId: serializedModelResponse.requestId,
    providerData: serializedModelResponse.providerData,
  };
}

type ContextOverrideStrategy = 'merge' | 'replace';
type RunStateContextOverrideOptions<TContext> = {
  contextOverride?: RunContext<TContext>;
  contextStrategy?: ContextOverrideStrategy;
};

async function buildRunStateFromString<
  TContext,
  TAgent extends Agent<any, any>,
>(
  initialAgent: TAgent,
  str: string,
  options: RunStateContextOverrideOptions<TContext> = {},
): Promise<RunState<TContext, TAgent>> {
  const [parsingError, jsonResult] = await safeExecute(() => JSON.parse(str));
  if (parsingError) {
    throw new UserError(
      `Failed to parse run state. ${parsingError instanceof Error ? parsingError.message : String(parsingError)}`,
    );
  }

  const currentSchemaVersion = jsonResult.$schemaVersion;
  if (!currentSchemaVersion) {
    throw new UserError('Run state is missing schema version');
  }
  if (
    !SUPPORTED_SCHEMA_VERSIONS.includes(
      currentSchemaVersion as SupportedSchemaVersion,
    )
  ) {
    throw new UserError(
      `Run state schema version ${currentSchemaVersion} is not supported. Please use version ${CURRENT_SCHEMA_VERSION}.`,
    );
  }
  const stateJson = SerializedRunState.parse(jsonResult);
  assertSchemaVersionSupportsToolSearch(
    currentSchemaVersion as SupportedSchemaVersion,
    stateJson,
  );
  return buildRunStateFromJson(initialAgent, stateJson, options);
}

function assertSchemaVersionSupportsToolSearch(
  schemaVersion: SupportedSchemaVersion,
  stateJson: z.infer<typeof SerializedRunState>,
): void {
  if (
    schemaVersion === '1.8' ||
    schemaVersion === '1.9' ||
    schemaVersion === '1.10' ||
    schemaVersion === CURRENT_SCHEMA_VERSION
  ) {
    return;
  }

  if (!containsSerializedToolSearchState(stateJson)) {
    return;
  }

  throw new UserError(
    `Run state schema version ${schemaVersion} does not support tool_search items. Please reserialize the run state with schema ${CURRENT_SCHEMA_VERSION}.`,
  );
}

async function buildRunStateFromJson<TContext, TAgent extends Agent<any, any>>(
  initialAgent: TAgent,
  stateJson: z.infer<typeof SerializedRunState>,
  options: RunStateContextOverrideOptions<TContext> = {},
): Promise<RunState<TContext, TAgent>> {
  const agentMap = schemaVersionSupportsAgentIdentity(
    stateJson.$schemaVersion as SupportedSchemaVersion,
  )
    ? buildAgentIdentityMap(initialAgent).byIdentity
    : buildAgentMap(initialAgent);
  const contextOverride = options.contextOverride;
  const contextStrategy = options.contextStrategy ?? 'merge';

  //
  // Rebuild the context
  //
  const context =
    contextOverride ??
    new RunContext<TContext>(stateJson.context.context as TContext);
  if (contextOverride) {
    if (contextStrategy === 'merge') {
      context._mergeApprovals(stateJson.context.approvals);
    }
  } else {
    context._rebuildApprovals(stateJson.context.approvals);
  }
  const shouldRestoreToolInput =
    !contextOverride || contextStrategy === 'merge';
  if (
    shouldRestoreToolInput &&
    typeof stateJson.context.toolInput !== 'undefined' &&
    typeof context.toolInput === 'undefined'
  ) {
    context.toolInput = stateJson.context.toolInput;
  }

  //
  // Find the current agent from the initial agent
  //
  const currentAgent = resolveSerializedAgent(stateJson.currentAgent, agentMap);

  const state = new RunState<TContext, TAgent>(
    context,
    '',
    initialAgent,
    stateJson.maxTurns,
  );
  state._currentAgent = currentAgent as TAgent;
  state._currentTurn = stateJson.currentTurn;
  state._currentTurnInProgress = stateJson.currentTurnInProgress ?? false;
  state._conversationId = stateJson.conversationId ?? undefined;
  state._previousResponseId = stateJson.previousResponseId ?? undefined;
  state._reasoningItemIdPolicy = stateJson.reasoningItemIdPolicy ?? undefined;

  // rebuild tool use tracker
  state._toolUseTracker = new AgentToolUseTracker();
  for (const [agentName, toolNames] of Object.entries(
    stateJson.toolUseTracker,
  )) {
    const agent = agentMap.get(agentName);
    if (!agent) {
      throw new UserError(`Agent ${agentName} not found`);
    }
    state._toolUseTracker.addToolUse(agent as TAgent, toolNames, {
      allowEmpty: true,
    });
  }

  state._pendingAgentToolRuns = new Map(
    Object.entries(stateJson.pendingAgentToolRuns ?? {}),
  );

  // rebuild current agent span
  if (stateJson.currentAgentSpan) {
    if (!stateJson.trace) {
      logger.warn('Trace is not set, skipping tracing setup');
    }

    const trace = getGlobalTraceProvider().createTrace({
      traceId: stateJson.trace?.id,
      name: stateJson.trace?.workflow_name,
      groupId: stateJson.trace?.group_id ?? undefined,
      metadata: stateJson.trace?.metadata,
      tracingApiKey: stateJson.trace?.tracing_api_key ?? undefined,
    });

    state._currentAgentSpan = deserializeSpan(
      trace,
      stateJson.currentAgentSpan,
    );
    state._trace = trace;
  }
  state._noActiveAgentRun = stateJson.noActiveAgentRun;

  state._inputGuardrailResults =
    stateJson.inputGuardrailResults as InputGuardrailResult[];
  state._outputGuardrailResults = stateJson.outputGuardrailResults.map((r) => ({
    ...r,
    agent: resolveSerializedAgent(r.agent, agentMap),
  })) as OutputGuardrailResult<any, any>[];
  state._toolInputGuardrailResults =
    stateJson.toolInputGuardrailResults as ToolInputGuardrailResult[];
  state._toolOutputGuardrailResults =
    stateJson.toolOutputGuardrailResults as ToolOutputGuardrailResult[];

  state._currentStep = stateJson.currentStep;

  state._originalInput = stateJson.originalInput as string | AgentInputItem[];
  state._modelResponses = stateJson.modelResponses.map(
    deserializeModelResponse,
  );
  state._lastTurnResponse = stateJson.lastModelResponse
    ? deserializeModelResponse(stateJson.lastModelResponse)
    : undefined;

  state._generatedItems = stateJson.generatedItems.map((item) =>
    deserializeItem(item, agentMap),
  );
  state._activeLocalCompaction = stateJson.activeLocalCompaction as
    | ActiveLocalCompactionState
    | undefined;
  state._currentTurnPersistedItemCount =
    stateJson.currentTurnPersistedItemCount ?? 0;
  state._sandbox = stateJson.sandbox ?? undefined;
  state._lastProcessedResponse = stateJson.lastProcessedResponse
    ? await deserializeProcessedResponse(
        agentMap,
        state,
        stateJson.lastProcessedResponse,
      )
    : undefined;

  if (stateJson.currentStep?.type === 'next_step_handoff') {
    state._currentStep = {
      type: 'next_step_handoff',
      newAgent: resolveSerializedAgent(
        stateJson.currentStep.newAgent,
        agentMap,
      ) as TAgent,
    };
  } else if (stateJson.currentStep?.type === 'next_step_interruption') {
    state._currentStep = {
      type: 'next_step_interruption',
      data: {
        ...stateJson.currentStep.data,
        interruptions: deserializeInterruptions(
          stateJson.currentStep.data?.interruptions,
          agentMap,
          state._currentAgent,
        ),
      },
    };
  }
  return state;
}

function deserializeInterruptionItem(
  serializedItem: unknown,
  agentMap: Map<string, Agent<any, any>>,
  currentAgent: Agent<any, any>,
): RunToolApprovalItem | undefined {
  if (serializedItem instanceof RunToolApprovalItem) {
    return serializedItem;
  }

  const parsed = itemSchema.safeParse(serializedItem);
  if (parsed.success) {
    if (parsed.data.type === 'tool_approval_item') {
      const mappedAgent = resolveSerializedAgent(
        parsed.data.agent,
        agentMap,
        currentAgent,
      );
      return new RunToolApprovalItem(
        parsed.data.rawItem,
        mappedAgent,
        parsed.data.toolName,
      );
    }

    const item = deserializeItem(parsed.data, agentMap);
    return item instanceof RunToolApprovalItem ? item : undefined;
  }

  if (!serializedItem || typeof serializedItem !== 'object') {
    return undefined;
  }

  const value = serializedItem as {
    rawItem?: unknown;
    toolName?: unknown;
    agent?: { name?: unknown; identity?: unknown };
  };

  if (!value.rawItem || typeof value.rawItem !== 'object') {
    return undefined;
  }

  const rawItem = value.rawItem as { type?: unknown; name?: unknown };
  if (
    rawItem.type !== 'function_call' &&
    rawItem.type !== 'hosted_tool_call' &&
    rawItem.type !== 'computer_call' &&
    rawItem.type !== 'shell_call'
  ) {
    return undefined;
  }

  const agentName =
    value.agent && typeof value.agent.name === 'string'
      ? value.agent.name
      : undefined;
  const agentIdentity =
    value.agent && typeof value.agent.identity === 'string'
      ? value.agent.identity
      : undefined;
  const mappedAgent =
    agentName || agentIdentity
      ? resolveSerializedAgent(
          {
            name: agentName ?? currentAgent.name,
            identity: agentIdentity,
          },
          agentMap,
          currentAgent,
        )
      : currentAgent;
  const toolName =
    typeof value.toolName === 'string'
      ? value.toolName
      : typeof rawItem.name === 'string'
        ? rawItem.name
        : undefined;

  return new RunToolApprovalItem(
    value.rawItem as RunToolApprovalItem['rawItem'],
    mappedAgent,
    toolName,
  );
}

function containsSerializedToolSearchState(
  stateJson: z.infer<typeof SerializedRunState>,
): boolean {
  return (
    containsToolSearchProtocolItems(stateJson.originalInput) ||
    containsToolSearchInModelResponses(stateJson.modelResponses) ||
    containsToolSearchInModelResponse(stateJson.lastModelResponse) ||
    containsToolSearchRunItems(stateJson.generatedItems) ||
    containsToolSearchInProcessedResponse(stateJson.lastProcessedResponse)
  );
}

function containsToolSearchProtocolItems(
  items: string | protocol.ModelItem[],
): boolean {
  return Array.isArray(items)
    ? items.some((item) => isToolSearchProtocolType(item.type))
    : false;
}

function isToolSearchProtocolType(type: unknown): boolean {
  return type === 'tool_search_call' || type === 'tool_search_output';
}

function containsToolSearchInModelResponses(
  modelResponses: z.infer<typeof modelResponseSchema>[],
): boolean {
  return modelResponses.some(containsToolSearchInModelResponse);
}

function containsToolSearchInModelResponse(
  modelResponse: z.infer<typeof modelResponseSchema> | undefined,
): boolean {
  return Boolean(
    modelResponse?.output.some((item) => isToolSearchProtocolType(item.type)),
  );
}

function containsToolSearchRunItems(
  _items: z.infer<typeof itemSchema>[] | undefined,
): boolean {
  return false;
}

function schemaVersionSupportsAgentIdentity(
  schemaVersion: SupportedSchemaVersion,
): boolean {
  return schemaVersion === '1.10' || schemaVersion === CURRENT_SCHEMA_VERSION;
}

export function deserializeSpan(
  trace: Trace,
  serializedSpan: SerializedSpanType,
): Span<any> {
  const spanData = serializedSpan.span_data;
  const previousSpan = serializedSpan.previous_span
    ? deserializeSpan(trace, serializedSpan.previous_span)
    : undefined;

  const span = getGlobalTraceProvider().createSpan(
    {
      spanId: serializedSpan.id,
      traceId: serializedSpan.trace_id,
      parentId: serializedSpan.parent_id ?? undefined,
      startedAt: serializedSpan.started_at ?? undefined,
      endedAt: serializedSpan.ended_at ?? undefined,
      data: spanData as any,
    },
    trace,
  );
  span.previousSpan = previousSpan;

  return span;
}

function deserializeInterruptions(
  serializedInterruptions: unknown,
  agentMap: Map<string, Agent<any, any>>,
  currentAgent: Agent<any, any>,
): RunToolApprovalItem[] {
  if (!Array.isArray(serializedInterruptions)) {
    return [];
  }

  return serializedInterruptions
    .map((item) => deserializeInterruptionItem(item, agentMap, currentAgent))
    .filter(
      (item): item is RunToolApprovalItem =>
        item instanceof RunToolApprovalItem,
    );
}

function containsToolSearchInProcessedResponse(
  processedResponse:
    | z.infer<typeof serializedProcessedResponseSchema>
    | undefined,
): boolean {
  return containsToolSearchRunItems(processedResponse?.newItems);
}
